import Link from 'next/link'
import { type Id, toast } from 'react-toastify'

import { SHOW_TOAST, ToastInfos } from '../types/toast'

const MAX_TOAST = 5
const toastList = new Set()
const initialState: ToastInfos = {
  toastIds: [],
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case SHOW_TOAST: {
      const msg = action.payload.msg
      const type = action.payload.type
      const autoClose = Number(action.payload.autoClose) || 3000
      if (type === 'info') {
        const toastId = toast.info(msg, {
          theme: 'colored',
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'warning') {
        const toastId = toast.warning(msg, {
          theme: 'colored',
          onClose: function () {
            toastList.delete(toastId)
          },
        })
        toastList.add(toastId)
      } else if (type === 'error') {
        const messageText = typeof msg === 'string' ? msg : msg != null ? String(msg) : 'Something went wrong.'
        const toastId = toast.error(
          <div className="flex flex-row items-start gap-3">
            <span
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#011D58]"
              aria-hidden
            >
              <span className="font-secondary text-[17px] font-bold leading-none text-white">!</span>
            </span>
            <p className="toast-lth-error-message min-w-0 flex-1 text-left">{messageText}</p>
          </div>,
          {
            theme: 'colored',
            icon: false,
            className: 'toast-lth-error',
            onClose: function () {
              toastList.delete(toastId)
            },
            autoClose,
          },
        )
        toastList.add(toastId)
      } else if (type === 'success') {
        const toastId = toast.success(msg, {
          theme: 'colored',
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'confirm') {
        const viewAllHref = action.payload.viewAllHref || '/my-followings'
        const CustomToastWithLink = () => (
          <div className="flex flex-row justify-between">
            <p className="toast-confirm-title mr-[10px]">{msg}</p>
            <Link href={viewAllHref} className="toast-confirm-view-all">
              View All
            </Link>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          closeButton: false,
          closeOnClick: false,
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'confirm_only_no_link') {
        const CustomToastWithLink = () => (
          <div className="flex flex-row justify-between">
            <p className="toast-confirm-title mr-[10px]">{msg}</p>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          closeButton: false,
          closeOnClick: false,
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'non_r_last_free_search') {
        const CustomToastWithLink = () => (
          <div className="flex flex-row justify-between">
            <p className="toast-confirm-title mr-[10px]">
              {msg}{' '}
              <Link href="/signup/" className="toast-confirm-view-all">
                Register a free account
              </Link>{' '}
              on LTH.
            </p>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          autoClose: 8000,
          onClose: function () {
            toastList.delete(toastId)
          },
          //autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'r_last_free_search' || type === 'vendor_last_free_search') {
        // Free and Standard vendors already have an account, so they are asked to upgrade.
        const CustomToastWithLink = () => (
          <div className="flex flex-row">
            <p className="toast-confirm-title">
              {msg}
              <Link
                href="https://hub.legaltechnologyhub.com/more-info"
                target="_blank"
                className="toast-confirm-view-all"
                onClick={() => {
                  toastList.delete(toastId)
                }}
              >
                {type === 'vendor_last_free_search' ? 'Upgrade to LTH Premium' : 'Sign up to LTH Premium'}
              </Link>{' '}
              for unlimited searches.
            </p>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          autoClose: 8000,
          onClose: function () {
            toastList.delete(toastId)
          },
          //autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'followedSubtopics') {
        const CustomToastWithLink = () => (
          <div className="flex flex-row justify-between">
            <p className="toast-confirm-title mr-[10px]">{msg}</p>
            <Link prefetch={false} href="/my-following-subtopics" className="toast-confirm-view-all">
              View All
            </Link>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: false,
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      } else if (type === 'followedRelatedSubtopics') {
        const CustomToastWithLink = () => (
          <div className="flex flex-row justify-between">
            <p className="toast-confirm-title mr-[10px]">{msg}</p>
          </div>
        )

        const toastId = toast.success(CustomToastWithLink, {
          theme: 'colored',
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: false,
          onClose: function () {
            toastList.delete(toastId)
          },
          autoClose,
        })
        toastList.add(toastId)
      }

      if (toastList.size > MAX_TOAST) {
        const first = toastList.values().next().value as Id | undefined
        toast.dismiss(first)
        if (first !== undefined) toastList.delete(first)
      }

      return {
        ...state,
      }
    }
    default:
      return state
  }
}

export default reducer
